import * as React from "react";
import { StaticImageData } from "next/image";

import { LinkWithLang } from "components/plasmic/shared/link-with-lang/client";
import Multimedia from "@stadium/shared/src/components/shared/multimedia";

import { BENTO_GRID_DEFAULTS, BG_01 } from "@stadium/shared/src/helpers/plasmic/static-slices";
import { type LinkWithLangType } from "@stadium/shared/src/constants/stores/routes";

import styles from "./main.module.scss";

export interface CardItem {
  cardImage?: string | StaticImageData;
  cardImageAlt?: string;
  cardButtonImage?: string | StaticImageData;
  cardButtonAlt?: string;
  cardButtonText?: string;
}

export interface BG01Props {
  contentHeading?: string;
  contentDescription?: string;
  buttonText?: string;
  buttonLinkType?: string;
  buttonOpenInNewTab?: boolean;
  buttonOtherUrl?: string;
  buttonCategorySlug?: string;
  card1?: CardItem;
  card2?: CardItem;
  card3?: CardItem;
  card4?: CardItem;
  className?: string;
}

interface CardProps {
  card: CardItem;
  containerClass: string;
  imageClass: string;
  overlayClass: string;
}

const Card: React.FC<Readonly<CardProps>> = ({
  card,
  containerClass,
  imageClass,
  overlayClass,
}) => (
  <div className={containerClass}>
    <Multimedia type="image" src={card.cardImage} alt={card.cardImageAlt} className={imageClass} />
    <div className={overlayClass}>
      <div className={styles.featuredButton}>
        <Multimedia
          type="image"
          src={card.cardButtonImage}
          alt={card.cardButtonAlt}
          className={styles.featuredButtonImage}
        />
        <span className={styles.buttonText}>{card.cardButtonText}</span>
      </div>
    </div>
  </div>
);

const BG01: React.FC<Readonly<BG01Props>> = ({
  contentHeading = BENTO_GRID_DEFAULTS[BG_01].contentHeading,
  contentDescription = BENTO_GRID_DEFAULTS[BG_01].contentDescription,
  buttonText = BENTO_GRID_DEFAULTS[BG_01].buttonText,
  buttonLinkType = BENTO_GRID_DEFAULTS[BG_01].buttonLinkType,
  buttonOpenInNewTab = BENTO_GRID_DEFAULTS[BG_01].buttonOpenInNewTab,
  buttonOtherUrl = BENTO_GRID_DEFAULTS[BG_01].buttonOtherUrl,
  buttonCategorySlug = BENTO_GRID_DEFAULTS[BG_01].buttonCategorySlug,
  card1 = BENTO_GRID_DEFAULTS[BG_01].cards[0],
  card2 = BENTO_GRID_DEFAULTS[BG_01].cards[1],
  card3 = BENTO_GRID_DEFAULTS[BG_01].cards[2],
  card4 = BENTO_GRID_DEFAULTS[BG_01].cards[3],
  className,
}) => {
  return (
    <section className={`${styles.sectionWrapper} ${className || ""}`}>
      <div className={styles.bg01Container}>
        <div className={styles.desktopGrid}>
          {/* Left Content Column */}
          <div className={styles.leftColumn}>
            <div className={styles.contentBox}>
              {contentHeading && <h2 className={styles.contentHeading}>{contentHeading}</h2>}
              <p className={styles.contentDescription}>{contentDescription}</p>
              {buttonText && buttonLinkType && (
                <LinkWithLang
                  type={buttonLinkType as LinkWithLangType}
                  data={{
                    code: buttonCategorySlug,
                    url: buttonOtherUrl,
                  }}
                  target={buttonOpenInNewTab ? "_blank" : "_self"}
                  showExternalLinkIcon={buttonOpenInNewTab}
                  className={styles.primaryOutlineBtn}
                >
                  {buttonText}
                </LinkWithLang>
              )}
            </div>

            {/* Card 1 - Featured */}
            <Card
              card={card1}
              containerClass={styles.featuredCard}
              imageClass={styles.featuredCardImage}
              overlayClass={styles.featuredOverlay}
            />
          </div>

          {/* Right Column - Three Cards */}
          <div className={styles.rightColumn}>
            {/* Card 2 */}
            <Card
              card={card2}
              containerClass={styles.rightUpperCard}
              imageClass={styles.rightCardImage}
              overlayClass={styles.rightButtonOverlay}
            />
            <div className={styles.rightBottomCards}>
              {/* Card 3 */}
              <Card
                card={card3}
                containerClass={styles.rightBottomOneCard}
                imageClass={styles.rightCardImage}
                overlayClass={styles.rightButtonOverlay}
              />

              {/* Card 4 */}
              <Card
                card={card4}
                containerClass={styles.rightBottomTwoCard}
                imageClass={styles.rightCardImage}
                overlayClass={styles.rightButtonOverlay}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BG01;
